import { Button } from "@/components/ui/button"
import { Calculator, House, Lock, MagnifyingGlass, SignOut, User, Wrench, X } from "@phosphor-icons/react"
import { User as UserType } from "@/App"
import { useState } from "react"
import Logo from "@/components/Logo"
import { useNavigate } from 'react-router-dom'

const HamburgerIcon = ({ size = 24 }: { size?: number }) => (
  <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="3" x2="21" y1="6" y2="6"/>
    <line x1="3" x2="21" y1="12" y2="12"/>
    <line x1="3" x2="21" y1="18" y2="18"/>
  </svg>
)

interface NavigationProps {
  currentUser: UserType | null
  currentPage: string
  onNavigate: (page: 'home' | 'login' | 'dashboard' | 'contractor' | 'search' | 'services' | 'how-it-works' | 'become-contractor' | 'about' | 'contact' | 'terms' | 'privacy' | 'cookies' | 'cost-calculator' | 'faq' | 'contractor-portal' | 'admin-dashboard') => void
  onLogout: () => void
  onLockPreview?: () => void
}

export function Navigation({ currentUser, currentPage, onNavigate, onLogout, onLockPreview }: NavigationProps) {
  const [menuOpen, setMenuOpen] = useState(false)
  const nav = useNavigate()

  const handleNavigation = (page: 'home' | 'login' | 'dashboard' | 'contractor' | 'search' | 'services' | 'how-it-works' | 'become-contractor' | 'about' | 'contact' | 'terms' | 'privacy' | 'cookies' | 'cost-calculator' | 'faq' | 'contractor-portal' | 'admin-dashboard') => {
    onNavigate(page)
    setMenuOpen(false)
  }

  const handleLogout = () => {
    onLogout()
    setMenuOpen(false)
  }

  const handleLockPreview = () => {
    onLockPreview?.()
    setMenuOpen(false)
  }

  return (
    <>
      <nav aria-label="Main navigation" className="sticky top-0 z-50 border-b border-white/10 bg-[#080808]/95 text-white shadow-[0_10px_35px_rgba(0,0,0,.35)] backdrop-blur-xl">
        <div className="h-1 bg-[#ff8c00]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-[4.5rem] items-center justify-between gap-6">
            {/* Logo */}
            <button 
              onClick={() => handleNavigation('home')}
              className="flex items-center gap-3 text-left transition-opacity hover:opacity-80"
              title="SealcoatPaving official company logo"
            >
              <Logo size={48} />
              <span className="hidden sm:block">
                <span className="brand-wordmark block text-sm tracking-[0.17em] text-white">SealcoatPaving</span>
                <span className="block text-[0.6rem] font-semibold uppercase tracking-[0.22em] text-[#ff8c00]">Contractor Network</span>
              </span>
            </button>

            <div className="hidden items-center gap-7 lg:flex">
              {[
                ['services', 'Services'],
                ['how-it-works', 'How it works'],
                ['cost-calculator', 'Cost calculator'],
              ].map(([page, label]) => (
                <button
                  key={page}
                  onClick={() => handleNavigation(page as Parameters<typeof handleNavigation>[0])}
                  className={`text-xs font-bold uppercase tracking-[0.12em] transition hover:text-[#ff8c00] ${currentPage === page ? 'text-[#ff8c00]' : 'text-white/60'}`}
                >
                  {label}
                </button>
              ))}
              <button onClick={() => { nav('/states'); setMenuOpen(false) }} className="text-xs font-bold uppercase tracking-[0.12em] text-white/60 transition hover:text-[#ff8c00]">Browse states</button>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => currentUser ? handleNavigation('dashboard') : handleNavigation('login')}
                className="hidden h-10 items-center gap-2 border border-white/15 px-4 text-xs font-bold uppercase tracking-[0.1em] text-white transition hover:border-[#ff8c00] hover:text-[#ff8c00] sm:inline-flex"
              >
                <User size={17} /> {currentUser ? 'Dashboard' : 'Sign in'}
              </button>
              <button
                className="relative z-[60] p-3 text-white/70 transition hover:bg-white/5 hover:text-[#ff8c00]"
                onClick={() => setMenuOpen(!menuOpen)}
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={menuOpen}
                aria-controls="side-menu"
              >
                <HamburgerIcon size={24} />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Overlay */}
      {menuOpen && (
        <div 
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 transition-opacity duration-300"
          onClick={() => setMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Side Menu */}
      <div
        id="side-menu"
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        onKeyDown={(e) => { if (e.key === 'Escape') setMenuOpen(false) }}
        className={`fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-[#0d0d0d]/98 backdrop-blur-xl border-l border-white/10 shadow-2xl transform transition-transform duration-300 ease-out z-50 ${
        menuOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        {/* Menu Header */}
        <div className="flex items-center justify-between p-6 border-b border-border/30">
          <div className="flex items-center gap-3">
            <Logo size={30} />
              <span className="brand-wordmark text-sm text-foreground">Navigation</span>
          </div>
          <button
            onClick={() => setMenuOpen(false)}
            className="p-2 text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-muted/50"
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        </div>

        {/* Menu Content */}
        <div className="flex flex-col h-full">
          <div className="flex-1 overflow-y-auto">
            <div className="p-6 space-y-6">
              {/* User Section */}
              {currentUser ? (
                <div className="pb-6 border-b border-border/30">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                      <User size={24} className="text-primary" />
                    </div>
                    <div>
                      <span className="text-foreground font-semibold block">{currentUser.name}</span>
                      <span className="text-muted-foreground text-sm">{currentUser.email}</span>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleLogout}
                    className="w-full justify-start hover:bg-destructive/10 hover:text-destructive hover:border-destructive/20"
                  >
                    <SignOut size={16} className="mr-2" />
                    Logout
                  </Button>
                </div>
              ) : (
                <div className="pb-6 border-b border-border/30">
                  <Button 
                    onClick={() => handleNavigation('login')} 
                    className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-3"
                  >
                    <User size={18} className="mr-2" />
                    Login with Google
                  </Button>
                </div>
              )}

              {onLockPreview ? <div className="pb-6 border-b border-border/30">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLockPreview}
                  className="w-full justify-start"
                >
                  <Lock size={16} className="mr-2" />
                  Lock Preview
                </Button>
              </div> : null}

              {/* Navigation Links */}
              <div className="space-y-2">
                <button 
                  onClick={() => handleNavigation('home')}
                  className={`w-full text-left text-base font-medium transition-all duration-200 py-4 px-4 rounded-xl flex items-center gap-3 ${
                    currentPage === 'home' 
                      ? 'text-primary bg-primary/10 border border-primary/20' 
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  🏠 <span>Home</span>
                </button>
                
                {currentUser && (
                  <button 
                    onClick={() => handleNavigation('dashboard')}
                    className={`w-full text-left text-base font-medium transition-all duration-200 py-4 px-4 rounded-xl flex items-center gap-3 ${
                      currentPage === 'dashboard' 
                        ? 'text-primary bg-primary/10 border border-primary/20' 
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    📊 <span>Dashboard</span>
                  </button>
                )}

                {currentUser && 'level' in currentUser && (currentUser as { level?: number }).level && (currentUser as { level?: number }).level! >= 2 && (
                  <button 
                    onClick={() => handleNavigation('contractor-portal')}
                    className={`w-full text-left text-base font-medium transition-all duration-200 py-4 px-4 rounded-xl flex items-center gap-3 ${
                      currentPage === 'contractor-portal' 
                        ? 'text-primary bg-primary/10 border border-primary/20' 
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    🔧 <span>Contractor Portal</span>
                  </button>
                )}

                {currentUser && ((currentUser as { isAdmin?: boolean }).isAdmin || ('level' in currentUser && (currentUser as { level?: number }).level && (currentUser as { level?: number }).level! >= 5)) && (
                  <button 
                    onClick={() => handleNavigation('admin-dashboard')}
                    className={`w-full text-left text-base font-medium transition-all duration-200 py-4 px-4 rounded-xl flex items-center gap-3 ${
                      currentPage === 'admin-dashboard' 
                        ? 'text-primary bg-primary/10 border border-primary/20' 
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    🛡️ <span>Admin Panel</span>
                  </button>
                )}
                
                <button 
                  onClick={() => handleNavigation('services')}
                  className={`w-full text-left text-base font-medium transition-all duration-200 py-4 px-4 rounded-xl flex items-center gap-3 ${
                    currentPage === 'services' 
                      ? 'text-primary bg-primary/10 border border-primary/20' 
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  🔧 <span>Services</span>
                </button>

                <button 
                  onClick={() => handleNavigation('cost-calculator')}
                  className={`w-full text-left text-base font-medium transition-all duration-200 py-4 px-4 rounded-xl flex items-center gap-3 ${
                    currentPage === 'cost-calculator' 
                      ? 'text-primary bg-primary/10 border border-primary/20' 
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  🧮 <span>Cost Calculator</span>
                </button>
                
                <button 
                  onClick={() => handleNavigation('how-it-works')}
                  className={`w-full text-left text-base font-medium transition-all duration-200 py-4 px-4 rounded-xl flex items-center gap-3 ${
                    currentPage === 'how-it-works' 
                      ? 'text-primary bg-primary/10 border border-primary/20' 
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  ❓ <span>How It Works</span>
                </button>

                <button 
                  onClick={() => { nav('/states'); setMenuOpen(false) }}
                  className={`w-full text-left text-base font-medium transition-all duration-200 py-4 px-4 rounded-xl flex items-center gap-3 ${
                    currentPage === 'browse-states' 
                      ? 'text-primary bg-primary/10 border border-primary/20' 
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  📍 <span>Browse by State</span>
                </button>

                <button 
                  onClick={() => handleNavigation('faq')}
                  className={`w-full text-left text-base font-medium transition-all duration-200 py-4 px-4 rounded-xl flex items-center gap-3 ${
                    currentPage === 'faq' 
                      ? 'text-primary bg-primary/10 border border-primary/20' 
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  📋 <span>FAQ</span>
                </button>
                
                <button 
                  onClick={() => handleNavigation('become-contractor')}
                  className={`w-full text-left text-base font-medium transition-all duration-200 py-4 px-4 rounded-xl flex items-center gap-3 ${
                    currentPage === 'become-contractor' 
                      ? 'text-primary bg-primary/10 border border-primary/20' 
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  💼 <span>Become a Contractor</span>
                </button>

                <button 
                  onClick={() => handleNavigation('about')}
                  className={`w-full text-left text-base font-medium transition-all duration-200 py-4 px-4 rounded-xl flex items-center gap-3 ${
                    currentPage === 'about' 
                      ? 'text-primary bg-primary/10 border border-primary/20' 
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  ℹ️ <span>About</span>
                </button>

                <button 
                  onClick={() => handleNavigation('contact')}
                  className={`w-full text-left text-base font-medium transition-all duration-200 py-4 px-4 rounded-xl flex items-center gap-3 ${
                    currentPage === 'contact' 
                      ? 'text-primary bg-primary/10 border border-primary/20' 
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  📞 <span>Contact</span>
                </button>
              </div>
            </div>
          </div>

          {/* Footer Links */}
          <div className="border-t border-border/30 p-6 space-y-2">
            <button 
              onClick={() => handleNavigation('terms')}
              className="w-full text-left text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
            >
              Terms of Service
            </button>
            <button 
              onClick={() => handleNavigation('privacy')}
              className="w-full text-left text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
            >
              Privacy Policy
            </button>
            <button 
              onClick={() => handleNavigation('cookies')}
              className="w-full text-left text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
            >
              Cookie Policy
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <nav aria-label="Mobile navigation" className="fixed bottom-0 left-0 right-0 z-50 border-t border-white/10 bg-[#080808]/95 backdrop-blur-lg shadow-[0_-8px_30px_rgba(0,0,0,.45)] md:hidden">
        <div className="flex items-center justify-around h-16 px-2">
          <button
            onClick={() => handleNavigation('home')}
            className={`flex flex-col items-center justify-center gap-0.5 flex-1 py-2 transition-colors ${
              currentPage === 'home' ? 'text-primary' : 'text-muted-foreground'
            }`}
          >
            <House size={22} weight={currentPage === 'home' ? 'fill' : 'regular'} />
            <span className="text-[10px] font-medium">Home</span>
          </button>
          <button
            onClick={() => handleNavigation('services')}
            className={`flex flex-col items-center justify-center gap-0.5 flex-1 py-2 transition-colors ${
              currentPage === 'services' ? 'text-primary' : 'text-muted-foreground'
            }`}
          >
            <Wrench size={22} weight={currentPage === 'services' ? 'fill' : 'regular'} />
            <span className="text-[10px] font-medium">Services</span>
          </button>
          <button
            onClick={() => {
              const el = document.getElementById('zip-search-input')
              if (el) { el.scrollIntoView({ behavior: 'smooth' }); el.focus() }
              else handleNavigation('home')
            }}
            className="flex flex-col items-center justify-center gap-0.5 flex-1 py-2 -mt-4"
          >
            <div className={`flex h-12 w-12 items-center justify-center rounded-full bg-primary text-black shadow-lg shadow-primary/30 ${currentPage === 'search' ? 'ring-2 ring-primary ring-offset-2 ring-offset-black' : ''}`}>
              <MagnifyingGlass size={24} weight="bold" />
            </div>
            <span className="text-[10px] font-medium text-primary">Search</span>
          </button>
          <button
            onClick={() => handleNavigation('cost-calculator')}
            className={`flex flex-col items-center justify-center gap-0.5 flex-1 py-2 transition-colors ${
              currentPage === 'cost-calculator' ? 'text-primary' : 'text-muted-foreground'
            }`}
          >
            <Calculator size={22} weight={currentPage === 'cost-calculator' ? 'fill' : 'regular'} />
            <span className="text-[10px] font-medium">Calculator</span>
          </button>
          <button
            onClick={() => currentUser ? handleNavigation('dashboard') : handleNavigation('login')}
            className={`flex flex-col items-center justify-center gap-0.5 flex-1 py-2 transition-colors ${
              currentPage === 'dashboard' || currentPage === 'login' ? 'text-primary' : 'text-muted-foreground'
            }`}
          >
            <User size={22} weight={currentPage === 'dashboard' ? 'fill' : 'regular'} />
            <span className="text-[10px] font-medium">{currentUser ? 'Account' : 'Login'}</span>
          </button>
        </div>
      </nav>
    </>
  )
}
